'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SavingsCalculator() {
  const t = useTranslations("tools.savingsCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    initialDeposit: 20000,
    monthlyContribution: 500,
    annualContribution: 0,
    interestRate: 3,
    compoundFrequency: 1,
    years: 10,
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const calculateSavings = (principal: number, monthlyContrib: number, annualContrib: number, rate: number, compoundFreq: number, years: number) => {
    const r = rate / 100;
    const n = compoundFreq;

    let balance = principal;
    let totalContributions = 0;
    const yearlyData = [];

    for (let year = 1; year <= years; year++) {
      let yearStartBalance = balance;
      let yearContributions = 0;

      for (let month = 1; month <= 12; month++) {
        if (monthlyContrib > 0) {
          balance += monthlyContrib;
          yearContributions += monthlyContrib;
        }

        const periodsInMonth = n / 12;
        for (let i = 0; i < periodsInMonth; i++) {
          balance = balance * (1 + r / n);
        }
      }

      if (annualContrib > 0) {
        balance += annualContrib;
        yearContributions += annualContrib;
      }

      totalContributions += yearContributions;

      const yearInterest = balance - yearStartBalance - yearContributions;

      yearlyData.push({
        year: year,
        balance: balance,
        contributions: yearContributions,
        interest: yearInterest
      });
    }

    const totalInterest = balance - principal - totalContributions;

    return {
      finalBalance: balance,
      totalContributions: totalContributions,
      totalInterest: totalInterest,
      yearlyData: yearlyData
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const result = calculateSavings(
      inputs.initialDeposit,
      inputs.monthlyContribution,
      inputs.annualContribution,
      inputs.interestRate,
      inputs.compoundFrequency,
      inputs.years
    );

    setResults(result);
  };

  useEffect(() => {
    const result = calculateSavings(
      inputs.initialDeposit,
      inputs.monthlyContribution,
      inputs.annualContribution,
      inputs.interestRate,
      inputs.compoundFrequency,
      inputs.years
    );
    setResults(result);
  }, []);

  const drawPieChart = () => {
    if (!results) return null;

    const principal = inputs.initialDeposit;
    const contributions = results.totalContributions;
    const interest = results.totalInterest;
    const total = principal + contributions + interest;

    if (total === 0) return null;

    const principalPercent = (principal / total) * 100;
    const contributionsPercent = (contributions / total) * 100;
    const interestPercent = (interest / total) * 100;

    const centerX = 140;
    const centerY = 100;
    const radius = 70;

    let currentAngle = -90;

    const slices = [
      { percent: principalPercent, color: '#2563eb', label: t('chartInitialDeposit'), value: principal },
      { percent: contributionsPercent, color: '#8bbc21', label: t('chartContributions'), value: contributions },
      { percent: interestPercent, color: '#f59e0b', label: t('chartInterest'), value: interest }
    ];

    const paths = slices.map((slice, index) => {
      if (slice.percent > 0) {
        const angle = (slice.percent / 100) * 360;
        const endAngle = currentAngle + angle;

        const x1 = centerX + radius * Math.cos(currentAngle * Math.PI / 180);
        const y1 = centerY + radius * Math.sin(currentAngle * Math.PI / 180);
        const x2 = centerX + radius * Math.cos(endAngle * Math.PI / 180);
        const y2 = centerY + radius * Math.sin(endAngle * Math.PI / 180);

        const largeArc = angle > 180 ? 1 : 0;

        const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

        currentAngle = endAngle;

        return (
          <path
            key={index}
            d={pathData}
            fill={slice.color}
            stroke="white"
            strokeWidth="2"
          />
        );
      }
      return null;
    });

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
          <svg width="280" height="200">
            {paths}
          </svg>
        </div>
        <div style={getResponsiveStyle(
          { display: 'grid', gap: '1rem' },
          { gridTemplateColumns: '1fr' },
          { gridTemplateColumns: 'repeat(2, 1fr)' },
          { gridTemplateColumns: 'repeat(3, 1fr)' }
        )}>
          {slices.map((slice, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '1rem', height: '1rem', borderRadius: '0.25rem', background: slice.color }}></div>
              <div style={{ fontSize: '0.9rem', color: colors.textSecondary }}>
                {slice.label}: {formatCurrency(slice.value)} ({slice.percent.toFixed(1)}%)
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = {
    display: 'block',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '1rem' }
  );

  const selectStyle = { ...inputStyle };

  const inputWithPrefixStyle = { position: 'relative' as const };

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 600
  };

  const inputWithSuffixStyle = { position: 'relative' as const };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 600
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultHighlightStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      borderLeft: '4px solid #22c55e'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    color: '#166534',
    fontWeight: 600,
    marginBottom: '0.5rem'
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: '#15803d', fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem 0',
    borderBottom: '1px solid #f1f5f9'
  };

  const resultItemLabelStyle = {
    color: '#64748b',
    fontWeight: 500
  };

  const resultItemValueStyle = {
    color: '#0f172a',
    fontWeight: 700
  };

  const chartContainerStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const scheduleTableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '1rem'
  };

  const tableHeaderStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#334155',
      borderBottom: '2px solid #e2e8f0'
    },
    { padding: '0.5rem', fontSize: '0.85rem' },
    { padding: '0.625rem', fontSize: '0.9rem' },
    { padding: '0.75rem', fontSize: '0.9rem' }
  );

  const tableCellStyle = getResponsiveStyle(
    { borderBottom: '1px solid #f1f5f9', color: '#475569' },
    { padding: '0.5rem', fontSize: '0.85rem' },
    { padding: '0.625rem', fontSize: '0.9rem' },
    { padding: '0.75rem', fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>💰 {t('toolTitle')}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorFormStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="initial-deposit" style={labelStyle}>{t('initialDepositLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="initial-deposit"
                      value={inputs.initialDeposit}
                      onChange={(e) => setInputs({...inputs, initialDeposit: parseFloat(e.target.value) || 0})}
                      step="0.01"
                      min="0"
                      required
                      style={{...inputStyle, paddingLeft: '2rem'}}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="monthly-contribution" style={labelStyle}>{t('monthlyContributionLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="monthly-contribution"
                      value={inputs.monthlyContribution}
                      onChange={(e) => setInputs({...inputs, monthlyContribution: parseFloat(e.target.value) || 0})}
                      step="0.01"
                      min="0"
                      required
                      style={{...inputStyle, paddingLeft: '2rem'}}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="annual-contribution" style={labelStyle}>{t('annualContributionLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="annual-contribution"
                      value={inputs.annualContribution}
                      onChange={(e) => setInputs({...inputs, annualContribution: parseFloat(e.target.value) || 0})}
                      step="0.01"
                      min="0"
                      required
                      style={{...inputStyle, paddingLeft: '2rem'}}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="interest-rate" style={labelStyle}>{t('interestRateLabel')}</label>
                  <div style={inputWithSuffixStyle}>
                    <input
                      type="number"
                      id="interest-rate"
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                      step="0.01"
                      min="0"
                      max="100"
                      required
                      style={{...inputStyle, paddingRight: '3.5rem'}}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="compound-frequency" style={labelStyle}>{t('compoundFrequencyLabel')}</label>
                  <select
                    id="compound-frequency"
                    value={inputs.compoundFrequency}
                    onChange={(e) => setInputs({...inputs, compoundFrequency: parseInt(e.target.value)})}
                    required
                    style={selectStyle}
                  >
                    <option value="12">{t('compoundMonthly')}</option>
                    <option value="4">{t('compoundQuarterly')}</option>
                    <option value="2">{t('compoundSemiAnnually')}</option>
                    <option value="1">{t('compoundAnnually')}</option>
                    <option value="365">{t('compoundDaily')}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="years" style={labelStyle}>{t('yearsLabel')}</label>
                  <div style={inputWithSuffixStyle}>
                    <input
                      type="number"
                      id="years"
                      value={inputs.years}
                      onChange={(e) => setInputs({...inputs, years: parseInt(e.target.value) || 1})}
                      step="1"
                      min="1"
                      max="100"
                      required
                      style={{...inputStyle, paddingRight: '4rem'}}
                    />
                    <span style={inputSuffixStyle}>{t('yearsSuffix')}</span>
                  </div>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {results && (
              <div style={resultsStyle}>
                <h3 style={h2Style}>{t('resultsTitle')}</h3>

                <div style={resultHighlightStyle}>
                  <span style={resultLabelStyle}>{t('finalBalanceLabel')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.finalBalance)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('initialDepositResult')}</span>
                  <span style={resultItemValueStyle}>{formatCurrency(inputs.initialDeposit)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('totalContributionsResult')}</span>
                  <span style={resultItemValueStyle}>{formatCurrency(results.totalContributions)}</span>
                </div>

                <div style={{...resultItemStyle, borderBottom: 'none'}}>
                  <span style={resultItemLabelStyle}>{t('totalInterestResult')}</span>
                  <span style={resultItemValueStyle}>{formatCurrency(results.totalInterest)}</span>
                </div>
              </div>
            )}
          </div>

          {results && (
            <>
              <div style={chartContainerStyle}>
                <h3 style={h2Style}>{t('breakdownTitle')}</h3>
                {drawPieChart()}
              </div>

              <div style={chartContainerStyle}>
                <h3 style={h2Style}>{t('scheduleTitle')}</h3>

                <div style={{ overflowX: 'auto' as const }}>
                  <table style={scheduleTableStyle}>
                    <thead>
                      <tr>
                        <th style={tableHeaderStyle}>{t('scheduleYear')}</th>
                        <th style={tableHeaderStyle}>{t('scheduleBalance')}</th>
                        <th style={tableHeaderStyle}>{t('scheduleContributions')}</th>
                        <th style={tableHeaderStyle}>{t('scheduleInterest')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.yearlyData.map((data: any) => (
                        <tr key={data.year} style={{ transition: 'background 0.2s ease' }}>
                          <td style={tableCellStyle}>{data.year}</td>
                          <td style={tableCellStyle}>{formatCurrency(data.balance)}</td>
                          <td style={tableCellStyle}>{formatCurrency(data.contributions)}</td>
                          <td style={tableCellStyle}>{formatCurrency(data.interest)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
          <li style={listItemStyle}>{t('howToUseStep6')}</li>
          <li style={listItemStyle}>{t('howToUseStep7')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightsCompoundTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsCompoundText')}</p>

        <h3 style={h3Style}>{t('insightsRatesTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsRatesText')}</p>

        <h3 style={h3Style}>{t('insightsEmergencyTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsEmergencyText')}</p>

        <h3 style={h3Style}>{t('insightsGoalTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsGoalText')}</p>

        <h3 style={h3Style}>{t('insightsBehaviorTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsBehaviorText')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsCompoundTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsCompoundText')}</p>

        <h3 style={h3Style}>{t('detailsFrequencyTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsFrequencyText')}</p>

        <h3 style={h3Style}>{t('detailsContributionsTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsContributionsText')}</p>

        <h3 style={h3Style}>{t('detailsLimitationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('detailsLimitation1')}</li>
          <li style={listItemStyle}>{t('detailsLimitation2')}</li>
          <li style={listItemStyle}>{t('detailsLimitation3')}</li>
          <li style={listItemStyle}>{t('detailsLimitation4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faqQuestion1')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer1')}</p>

        <h3 style={h3Style}>{t('faqQuestion2')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer2')}</p>

        <h3 style={h3Style}>{t('faqQuestion3')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer3')}</p>

        <h3 style={h3Style}>{t('faqQuestion4')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer4')}</p>

        <h3 style={h3Style}>{t('faqQuestion5')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer5')}</p>

        <h3 style={h3Style}>{t('faqQuestion6')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer6')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.bankrate.com/banking/savings/simple-savings-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Simple Savings Calculator | Bankrate
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.creditkarma.com/calculators/money/savings" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Savings Calculator - Intuit Credit Karma
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.umcu.org/learn/resources/calculators/save-toward-a-goal-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Save Toward a Goal Calculator - UMCU
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.nerdwallet.com/banking/calculator/savings-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Savings Calculator - NerdWallet
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.investor.gov/financial-tools-calculators/calculators/savings-goal-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Savings Goal Calculator - Investor.gov
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}